'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { usePersistedState } from '@/hooks/usePersistedState';
import { DEFAULT_SETTINGS } from './defaultSettings';
import { GLOBAL_SETTINGS_KEYS } from './settingKeys';
// Define global settings structure
export type GlobalSettingsValue = Record<string, unknown>;

// Global settings context type
export type GlobalSettingsContextType = {
  getSettingValue: (key: string) => unknown;
  updateSetting: (key: string, value: unknown) => void;
};

// Create the context
export const GlobalSettingsContext = createContext<GlobalSettingsContextType | null>(null);

// Hook to use the global settings
export function useGlobalSettings() {
  const context = useContext(GlobalSettingsContext);
  if (!context) {
    throw new Error('useGlobalSettings must be used within a GlobalSettingsProvider');
  }
  return context;
}

// Props for the provider
type GlobalSettingsProviderProps = {
  children: React.ReactNode;
  storageKey?: string;
};

// The provider component
export function GlobalSettingsProvider({ children }: GlobalSettingsProviderProps) {
  // Single source of truth for all settings
  const [persistedSettings, setPersistedSettings] = usePersistedState<GlobalSettingsValue>(
    DEFAULT_SETTINGS,
    GLOBAL_SETTINGS_KEYS,
  );

  // Context value with memoization to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      getSettingValue: (key: string) => persistedSettings[key],
      updateSetting: (key: string, value: unknown) => {
        setPersistedSettings((prev) => ({
          ...prev,
          [key]: value,
        }));
      },
    }),
    [persistedSettings, setPersistedSettings],
  );

  return (
    <GlobalSettingsContext.Provider value={contextValue}>{children}</GlobalSettingsContext.Provider>
  );
}
